import { useEffect, useState } from 'react';
import { authApi, consulentiApi, commesseApi, commesseUtentiApi, offerteApi, offerteUtentiApi } from '../api';
import Modal from '../components/Modal';
import FirmaUtente from '../components/FirmaUtente';

const empty = { username: '', password: '', nome: '', cognome: '', iniziali: '', email: '', ruolo: 'esterno', consulente_id: '', puo_emettere_offerte: false, puo_scaricare_offerte: false, puo_scaricare_db: false };

const suggerisciIniziali = (nome, cognome) => `${(nome||'').trim()[0]||''}${(cognome||'').trim()[0]||''}`.toUpperCase();

export default function GestioneUtenti() {
  const [users, setUsers] = useState([]);
  const [consulenti, setConsulenti] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [inizialiTouched, setInizialiTouched] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pwOriginale, setPwOriginale] = useState('');       // password salvata, per capire se è cambiata
  const [pwNonDisponibile, setPwNonDisponibile] = useState(false);
  const [copiato, setCopiato] = useState(false);
  const [pwVisibili, setPwVisibili] = useState({});          // password mostrate in elenco
  // Pannello assegnazione commesse
  const [assegnaUser, setAssegnaUser] = useState(null);
  const [cercaCommessa, setCercaCommessa] = useState('');
  const [assegnate, setAssegnate] = useState([]);
  // Pannello assegnazione offerte (visibilità per i consulenti)
  const [offerteUser, setOfferteUser] = useState(null);
  const [offerte, setOfferte] = useState([]);
  const [offerteAssegnate, setOfferteAssegnate] = useState([]);
  const [cercaOfferta, setCercaOfferta] = useState('');

  const load = () => authApi.users().then(setUsers);
  useEffect(() => {
    load();
    consulentiApi.list().then(setConsulenti);
    commesseApi.list({ stato: 'attiva' }).then(setCommesse);
  }, []);

  const openOfferte = async (u) => {
    setOfferteUser(u);
    setCercaOfferta('');
    const [tutte, assegnateIds] = await Promise.all([
      offerteApi.list(),
      offerteUtentiApi.perUtente(u.id),
    ]);
    setOfferte(tutte);
    setOfferteAssegnate(assegnateIds);
  };

  const toggleOfferta = async (offerta_id) => {
    if (offerteAssegnate.includes(offerta_id)) {
      await offerteUtentiApi.remove(offerta_id, offerteUser.id);
      setOfferteAssegnate(p => p.filter(x => x !== offerta_id));
    } else {
      await offerteUtentiApi.add(offerta_id, offerteUser.id);
      setOfferteAssegnate(p => [...p, offerta_id]);
    }
  };

  const openNew = () => {
    setForm(empty); setInizialiTouched(false); setShowPassword(false);
    setPwOriginale(''); setPwNonDisponibile(false);
    setModal({ mode: 'new' });
  };

  const openEdit = async (u) => {
    setForm({ ...u, password: '', consulente_id: u.consulente_id || '',
      puo_emettere_offerte: !!u.puo_emettere_offerte,
      puo_scaricare_offerte: !!u.puo_scaricare_offerte,
      puo_scaricare_db: !!u.puo_scaricare_db,
    });
    setInizialiTouched(true); // in modifica non risuggerire automaticamente
    setShowPassword(false);
    setModal({ mode: 'edit', id: u.id });
    // Recupera la password salvata: mostrarla evita di doverne generare
    // una nuova solo perché il campo appariva vuoto
    try {
      const r = await authApi.password(u.id);
      setPwOriginale(r.password || '');
      setPwNonDisponibile(!r.disponibile);
      if (r.password) setForm(p => ({ ...p, password: r.password }));
    } catch { setPwOriginale(''); setPwNonDisponibile(true); }
  };

  const generaPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    const pwd = Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    setForm(p => ({ ...p, password: pwd }));
    setShowPassword(true);
  };

  const setNomeCognome = (key, value) => {
    setForm(p => {
      const next = { ...p, [key]: value };
      if (!inizialiTouched) next.iniziali = suggerisciIniziali(next.nome, next.cognome);
      return next;
    });
  };

  const save = async () => {
    if (!form.nome) return alert('Inserire il nome');
    if (!form.cognome) return alert('Inserire il cognome');
    if (!form.iniziali) return alert('Inserire le iniziali');
    if (modal.mode === 'new' && !form.username) return alert('Inserire username');
    if (modal.mode === 'new' && !form.password) return alert('Inserire una password');
    setSaving(true);
    try {
      const payload = { ...form, iniziali: form.iniziali.toUpperCase(), consulente_id: form.consulente_id || undefined };
      if (modal.mode === 'new') await authApi.createUser(payload);
      else await authApi.updateUser(modal.id, { ...payload, attivo: form.attivo ?? true });
      setModal(null); load();
    } catch (e) { alert(e); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm('Eliminare questo utente?')) return;
    await authApi.deleteUser(id); load();
  };

  // Gestione assegnazioni commesse
  const openAssegna = async (u) => {
    setAssegnaUser(u);
    setCercaCommessa('');
    // Una sola chiamata: prima si interrogava il server una volta per commessa
    setAssegnate(await commesseUtentiApi.perUtente(u.id));
  };

  const toggleCommessa = async (commessa_id) => {
    if (assegnate.includes(commessa_id)) {
      await commesseUtentiApi.remove(commessa_id, assegnaUser.id);
      setAssegnate(p => p.filter(x => x !== commessa_id));
    } else {
      await commesseUtentiApi.add(commessa_id, assegnaUser.id);
      setAssegnate(p => [...p, commessa_id]);
    }
  };

  const f = k => ({ value: form[k] ?? '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="page-title">Utenti e Accessi</h1>
        <button className="btn-primary" onClick={openNew}>+ Nuovo utente</button>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-xs text-gray-500 text-left">
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Iniziali</th>
              <th className="px-4 py-3">Username</th>
              <th className="px-4 py-3">Ruolo</th>
              <th className="px-4 py-3">Permessi</th>
              <th className="px-4 py-3">Stato</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Nessun utente</td></tr>}
            {users.map(u => (
              <tr key={u.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">{u.nome_completo || `${u.nome} ${u.cognome||''}`.trim()}</td>
                <td className="px-4 py-3 font-mono text-xs text-gray-500">{u.iniziali || '—'}</td>
                <td className="px-4 py-3 font-mono text-gray-600">
                  <div>{u.username}</div>
                  <button
                    className="text-[11px] text-brand-600 hover:underline font-sans"
                    onClick={async () => {
                      if (pwVisibili[u.id] !== undefined) { setPwVisibili(p => ({ ...p, [u.id]: undefined })); return; }
                      try {
                        const r = await authApi.password(u.id);
                        setPwVisibili(p => ({ ...p, [u.id]: r.disponibile ? r.password : null }));
                      } catch { setPwVisibili(p => ({ ...p, [u.id]: null })); }
                    }}>
                    {pwVisibili[u.id] !== undefined ? 'nascondi' : 'mostra password'}
                  </button>
                  {pwVisibili[u.id] !== undefined && (
                    <div className="mt-0.5">
                      {pwVisibili[u.id] === null
                        ? <span className="text-[11px] text-amber-600 font-sans">non memorizzata</span>
                        : <span className="text-xs bg-yellow-50 border border-yellow-200 rounded px-1.5 py-0.5 select-all">
                            {pwVisibili[u.id]}
                          </span>}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    u.ruolo === 'admin' ? 'bg-purple-100 text-purple-700'
                    : u.ruolo === 'interno' ? 'bg-teal-100 text-teal-700'
                    : 'bg-blue-100 text-blue-700'}`}>
                    {u.ruolo}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-500 space-y-0.5">
                  {u.ruolo === 'admin' ? (
                    <span className="text-purple-600 font-medium">Tutti</span>
                  ) : (
                    <>
                      {u.puo_emettere_offerte && <div>✓ Offerte</div>}
                      {u.puo_scaricare_offerte && <div>✓ Download offerte</div>}
                      {u.puo_scaricare_db && <div>✓ Export DB</div>}
                      {!u.puo_emettere_offerte && !u.puo_scaricare_offerte && !u.puo_scaricare_db && <span className="text-gray-300">—</span>}
                    </>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${u.attivo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {u.attivo ? 'Attivo' : 'Disattivato'}
                  </span>
                </td>
                <td className="px-4 py-3 flex gap-1 justify-end">
                  {u.ruolo === 'esterno' && (
                    <>
                      <button className="btn-secondary btn-sm" onClick={() => openAssegna(u)}>🔗 Commesse</button>
                      <button className="btn-secondary btn-sm" onClick={() => openOfferte(u)}>📄 Offerte</button>
                    </>
                  )}
                  {/* La firma serve a chi emette offerte: è lì che finisce. */}
                  {(u.ruolo === 'admin' || u.puo_emettere_offerte) && (
                    <FirmaUtente utente={u} onCambiato={load} />
                  )}
                  <button className="btn-secondary btn-sm" onClick={() => openEdit(u)}>Modifica</button>
                  <button className="btn-danger btn-sm" onClick={() => del(u.id)}>Elimina</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal crea/modifica utente */}
      {modal && (
        <Modal title={modal.mode === 'new' ? 'Nuovo utente' : 'Modifica utente'} onClose={() => setModal(null)} size="md">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Nome *</label><input className="input" value={form.nome||''} onChange={e => setNomeCognome('nome', e.target.value)} /></div>
            <div><label className="label">Cognome *</label><input className="input" value={form.cognome||''} onChange={e => setNomeCognome('cognome', e.target.value)} /></div>
            <div>
              <label className="label">
                Iniziali *
                <span className="ml-1 text-xs text-gray-400 font-normal">(usate per la numerazione offerte, es. FB)</span>
              </label>
              <input
                className="input font-mono uppercase"
                maxLength={4}
                value={form.iniziali || ''}
                onChange={e => { setInizialiTouched(true); setForm(p => ({ ...p, iniziali: e.target.value.toUpperCase() })); }}
              />
            </div>
            <div><label className="label">Username *</label><input className="input" {...f('username')} disabled={modal.mode === 'edit'} /></div>
            <div>
              <label className="label">
                {modal.mode === 'new' ? 'Password *' : 'Password di accesso'}
              </label>
              <div className="flex gap-1">
                <div className="relative flex-1">
                  <input
                    className="input pr-16 font-mono"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder={pwNonDisponibile ? 'non memorizzata — scrivine una nuova' : ''}
                    {...f('password')}
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1.5">
                    <button type="button" tabIndex={-1}
                      onClick={() => setShowPassword(v => !v)}
                      className="text-gray-400 hover:text-gray-600 text-sm"
                      title={showPassword ? 'Nascondi' : 'Mostra'}>
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                    <button type="button" tabIndex={-1}
                      disabled={!form.password}
                      onClick={() => {
                        navigator.clipboard?.writeText(form.password);
                        setCopiato(true); setTimeout(() => setCopiato(false), 1500);
                      }}
                      className="text-gray-400 hover:text-gray-600 text-sm disabled:opacity-30"
                      title="Copia negli appunti">📋</button>
                  </div>
                </div>
                <button type="button" className="btn-secondary btn-sm whitespace-nowrap" onClick={generaPassword}>
                  Genera
                </button>
              </div>

              <p className="text-xs mt-1">
                {copiato ? (
                  <span className="text-green-600 font-medium">Copiata negli appunti</span>
                ) : modal.mode === 'new' ? (
                  <span className="text-gray-400">Scrivila tu oppure premi <strong>Genera</strong> per una casuale sicura.</span>
                ) : pwNonDisponibile ? (
                  <span className="text-amber-600">
                    Password impostata prima di questa funzione: non è consultabile.
                    Scrivine una nuova per poterla vedere d'ora in avanti.
                  </span>
                ) : form.password !== pwOriginale ? (
                  <span className="text-amber-600">Modificata: salvando, la vecchia password smetterà di funzionare.</span>
                ) : (
                  <span className="text-gray-400">
                    Questa è la password attuale dell'utente. Modificala solo se vuoi cambiarla.
                  </span>
                )}
              </p>
            </div>
            <div><label className="label">Email</label><input className="input" type="email" {...f('email')} /></div>
            <div>
              <label className="label">Ruolo</label>
              <select className="input" value={form.ruolo} onChange={e => setForm(p => ({ ...p, ruolo: e.target.value }))}>
                <option value="esterno">Esterno (collaboratore)</option>
                <option value="interno">Interno (dipendente Sinthema)</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="label">Collega a consulente (opzionale)</label>
              <select className="input" value={form.consulente_id || ''} onChange={e => setForm(p => ({ ...p, consulente_id: e.target.value }))}>
                <option value="">— Nessuno —</option>
                {consulenti.map(c => <option key={c.id} value={c.id}>{c.nome} {c.cognome}</option>)}
              </select>
            </div>
            {/* Permessi */}
            <div className="col-span-2">
              <label className="label">Permessi</label>
              {form.ruolo === 'admin' ? (
                <p className="text-xs text-purple-600 bg-purple-50 rounded px-3 py-2">L'admin ha automaticamente accesso a tutto.</p>
              ) : (
                <div className="space-y-2 p-3 bg-gray-50 rounded-lg border">
                  {[
                    { key: 'puo_emettere_offerte', label: 'Può emettere offerte (crea / modifica)' },
                    { key: 'puo_scaricare_offerte', label: 'Può scaricare le offerte (PDF / export)' },
                    { key: 'puo_scaricare_db', label: 'Può esportare il database' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.checked }))} className="w-4 h-4" />
                      <span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {modal.mode === 'edit' && (
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="attivo" checked={form.attivo ?? true} onChange={e => setForm(p => ({ ...p, attivo: e.target.checked }))} />
                <label htmlFor="attivo" className="text-sm text-gray-700">Utente attivo</label>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvo…' : 'Salva'}</button>
          </div>
        </Modal>
      )}

      {/* Pannello assegnazione commesse */}
      {assegnaUser && (
        <Modal title={`Commesse di ${assegnaUser.nome_completo || assegnaUser.nome}`} onClose={() => setAssegnaUser(null)} size="lg">
          <p className="text-sm text-gray-500 mb-3">
            Seleziona le commesse a cui <strong>{assegnaUser.nome_completo || assegnaUser.nome}</strong> può accedere per inviare rendiconti e SAL:
          </p>
          <input
            className="input mb-3"
            placeholder="Cerca per numero, titolo o cliente…"
            value={cercaCommessa}
            onChange={e => setCercaCommessa(e.target.value)}
          />
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {commesse.length === 0 && <p className="text-gray-400 text-sm">Nessuna commessa attiva</p>}
            {(() => {
              const q = cercaCommessa.trim().toLowerCase();
              // Le già assegnate restano sempre visibili, anche fuori dalla ricerca:
              // altrimenti filtrando sembrerebbero sparite.
              const filtrate = commesse.filter(c =>
                assegnate.includes(c.id) ||
                !q || `${c.numero} ${c.titolo} ${c.cliente_nome || ''}`.toLowerCase().includes(q));
              if (filtrate.length === 0) {
                return <p className="text-gray-400 text-sm py-4 text-center">Nessuna commessa corrisponde alla ricerca</p>;
              }
              return filtrate.map(c => (
                <label key={c.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${assegnate.includes(c.id) ? 'bg-brand-50 border-brand-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                  <input type="checkbox" checked={assegnate.includes(c.id)} onChange={() => toggleCommessa(c.id)} className="w-4 h-4 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-gray-800 truncate">
                      <span className="font-mono text-xs text-gray-500 mr-2">{c.numero}</span>{c.titolo}
                    </div>
                    <div className="text-xs text-gray-400">
                      {c.cliente_nome}
                      {c.modalita_esecuzione === 'economia' && (
                        <span className="ml-2 text-purple-600">in economia</span>
                      )}
                    </div>
                  </div>
                </label>
              ));
            })()}
          </div>
          <div className="flex items-center justify-between mt-4">
            <span className="text-xs text-gray-500">{assegnate.length} commesse assegnate</span>
            <button className="btn-primary" onClick={() => setAssegnaUser(null)}>Fatto</button>
          </div>
        </Modal>
      )}

      {/* Pannello assegnazione offerte visibili al consulente */}
      {offerteUser && (
        <Modal title={`Offerte visibili a ${offerteUser.nome_completo || offerteUser.nome}`} onClose={() => setOfferteUser(null)} size="lg">
          <div className="text-xs text-gray-500 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 mb-3">
            🔒 Il consulente vedrà <strong>solo numero, titolo e cliente</strong> delle offerte selezionate — mai gli importi.
            Servono per permettergli di collegare le proprie offerte al lavoro giusto. Senza selezioni non vede nulla.
          </div>
          <input
            className="input mb-3"
            placeholder="Cerca per numero, titolo o cliente…"
            value={cercaOfferta}
            onChange={e => setCercaOfferta(e.target.value)}
          />
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {offerte.length === 0 && <p className="text-gray-400 text-sm">Nessuna offerta disponibile</p>}
            {offerte
              .filter(o => {
                const q = cercaOfferta.trim().toLowerCase();
                if (!q) return true;
                return `${o.numero} ${o.titolo} ${o.cliente_nome || ''}`.toLowerCase().includes(q);
              })
              .map(o => (
                <label key={o.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${offerteAssegnate.includes(o.id) ? 'bg-brand-50 border-brand-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                  <input type="checkbox" checked={offerteAssegnate.includes(o.id)} onChange={() => toggleOfferta(o.id)} className="w-4 h-4 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-gray-800 truncate">
                      <span className="font-mono text-xs text-gray-500 mr-2">{o.numero}</span>
                      {o.titolo}
                    </div>
                    <div className="text-xs text-gray-400">{o.cliente_nome}</div>
                  </div>
                </label>
              ))}
          </div>
          <div className="flex items-center justify-between mt-4">
            <span className="text-xs text-gray-500">{offerteAssegnate.length} offerte assegnate</span>
            <button className="btn-primary" onClick={() => setOfferteUser(null)}>Fatto</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
